// Packaging Container Types
export interface PackagingContainer {
  id: string;
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxWeightKg?: number;
  maxUnits?: number;
  /** Peso del envase vacío en kg, usado para calcular peso neto a partir del bruto. */
  tareWeightKg?: number;
  isActive: boolean;
  createdAt: string;
}

export interface CreatePackagingContainerRequest {
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxWeightKg?: number;
  maxUnits?: number;
}

export interface AssignPackagingRequest {
  weightPerBoxKg?: number;
}

// Pallet Type
export interface PalletType {
  id: string;
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  maxHeightCm: number;
  maxWeightKg: number;
  isActive: boolean;
  createdAt: string;
}

export interface CreatePalletTypeRequest {
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  maxHeightCm: number;
  maxWeightKg: number;
}

// Loading Plan Types
export enum LoadingPlanStatus {
  Draft = "Draft",
  Calculated = "Calculated",
  Approved = "Approved",
  InProgress = "InProgress",
  Completed = "Completed",
  Outdated = "Outdated",
}

/** Claves de algoritmo de distribución disponibles. */
export const PACKING_ALGORITHMS = [
  { key: "ShelfNextFit", label: "Estándar (Fila a fila)", description: "Rellena filas → capas → altura" },
  { key: "HeavyFirst",   label: "Más pesado abajo",       description: "Los más pesados se colocan en la base" },
  { key: "GroupByProduct", label: "Por producto",         description: "Agrupa cajas del mismo producto en zonas contiguas" },
] as const;

export type PackingAlgorithmKey = typeof PACKING_ALGORITHMS[number]["key"];

export interface LoadingPlanMetrics {
  totalVirtualPallets: number;
  totalPhysicalPallets: number;
  totalPallets: number;
  maxPallets: number;
  totalWeightKg: number;
  capacityUtilizationPercent: number;
}

export interface PalletItemDto {
  productName: string;
  packagingName: string;
  numberOfBoxes: number;
  weightKg: number;
  stackingLevel: number;
}

export interface VirtualPalletDto {
  id: string;
  sequenceNumber: number;
  loadingOrder: number;
  totalWeightKg: number;
  estimatedHeightCm: number;
  totalBoxes: number;
  workOrderIds?: string[];
  customerNames?: string[];
  items: PalletItemDto[];
}

export interface PhysicalPalletDto {
  id: string;
  palletCode: string;
  status: string;
  loadingOrder: number;
  actualWeightKg: number;
  totalBoxes: number;
  workOrderIds?: string[];
  customerNames?: string[];
  items: PalletItemDto[];
}

export interface LoadingPlanResponse {
  id: string;
  routeId: string;
  routeName: string;
  vehicleId: string;
  vehicleName: string;
  palletTypeId: string;
  palletTypeName: string;
  plannedDate: string;
  orderGroups?: string[][];
  groupAlgorithms?: (string | null)[];
  status: LoadingPlanStatus;
  metrics: LoadingPlanMetrics;
  virtualPallets: VirtualPalletDto[];
  physicalPallets: PhysicalPalletDto[];
  createdAt: string;
  approvedAt?: string;
  completedAt?: string;
}

export interface GenerateLoadingPlanRequest {
  routeId: string;
  plannedDate: string;
  vehicleId?: string;
  palletTypeId?: string;
  orderGroups?: string[][];
  groupAlgorithms?: (string | null)[];
}

export interface RecalculateLoadingPlanRequest {
  orderGroups?: string[][];
  groupAlgorithms?: (string | null)[];
}

export interface ConvertToPhysicalRequest {
  palletCode: string;
}

export interface BoxPosition {
  length: number;
  width: number;
  height: number;
  x: number;
  y: number;
  z: number;
  productName: string;
  color: string;
  packagingId?: string;
  model3DUrl?: string;
  isSeparator?: boolean;
  layerIndex?: number;
  /** Estrato de fragilidad: "Base", "Medio", "Tope" */
  stratum?: string;
  /** Prioridad de apilado (1 = más resistente, 5 = más frágil) */
  stackingPriority?: number;
  /** Tipo de material: PlasticoRigido, PlasticoLigero, Madera, Carton, Poliestireno */
  materialType?: string;
}

export interface DebugLayerProduct {
  productName: string;
  packagingName: string;
  boxCount: number;
}

export interface DebugLayerInfo {
  layerIndex: number;
  stratum: string;
  heightCm: number;
  zOffsetCm: number;
  weightKg: number;
  cumulativeWeightKg: number;
  coveragePercent: number;
  boxCount: number;
  hasSeparatorBelow: boolean;
  isMixed: boolean;
  isTopLayer: boolean;
  stackingFamily?: string;
  products: DebugLayerProduct[];
}

export interface PalletLayout {
  length: number;
  width: number;
  baseHeight?: number;
  items: BoxPosition[];
  palletModelUrl?: string;
  debugLayers?: DebugLayerInfo[];
}

export interface PhaseSnapshot {
  palletIndex: number;
  label: string;
  layout: BoxPosition[];
  heightCm: number;
  weightKg: number;
  totalBoxes: number;
}

export interface AlgorithmPhaseDebug {
  phase: number;
  name: string;
  description: string;
  summary: string;
  details: any;
  snapshots?: PhaseSnapshot[];
}

// Pallet Preview (for order form)
export interface PalletPreviewItemDto {
  /** Línea de comanda a la que pertenecen estas cajas (necesario para asociar allocations). */
  workOrderLineId?: string | null;
  packagingContainerId?: string | null;
  productName: string;
  packagingName: string;
  numberOfBoxes: number;
  weightKg: number;
}

export interface PalletPreviewDto {
  /** Identidad estable del palet — sobrevive a recálculos/redistribuciones. */
  id: string;
  /** Si está cerrado, el editor de allocations queda read-only y se ofrece "Reabrir". */
  closed?: boolean;
  closedAt?: string | null;
  /** True si el palet fue abierto manualmente por un mozo (no calculado). */
  isManual?: boolean;
  openedByUserId?: string | null;
  openedByUserName?: string | null;
  openedAt?: string | null;
  /** Peso del palet vacío + separadores + plásticos. */
  deadWeightKg?: number;
  sequenceNumber: number;
  totalBoxes: number;
  totalWeightKg: number;
  estimatedHeightCm: number;
  items: PalletPreviewItemDto[];
  layout: BoxPosition[];
  debugLayers?: DebugLayerInfo[];
}

export interface PalletPreviewResponse {
  pallets: PalletPreviewDto[];
  totalPallets: number;
  warnings: string[];
  linesWithoutPackaging: string[];
  palletLengthCm: number;
  palletWidthCm: number;
  palletBaseHeightCm: number;
  palletModelUrl?: string;
  debugTrace?: AlgorithmPhaseDebug[];
}

export interface SavedPalletResponse {
  layout: PalletPreviewResponse | null;
  calculatedAt: string | null;
  manuallyEdited: boolean;
  algorithm: string | null;
}

// Phase 8 — manual assignment
export interface ManualAssignmentItem {
  productName: string;
  packagingName: string;
  quantityPerPallet: number[];
}

export interface ManualAssignmentRequest {
  assignments: ManualAssignmentItem[];
  /** Ids estables de los palets en orden de columna. Se preservan los Ids de
   *  los palets que el usuario no ha eliminado; los nuevos se generan en backend. */
  palletIds?: string[];
}

export interface ManualAssignmentValidationError {
  code: string;
  message: string;
  productName?: string;
  packagingName?: string;
}
